import React, { useContext } from 'react';
import dayjs from 'dayjs';

import AssetInventoryV2Context from '@totem/components/services/assetInventory/v2/AssetInventoryV2Context';
import BarList, {
  BarItem,
} from '@totem/components/services/assetInventory/v2/BarList';
import {
  formatDay,
  formatMonth,
} from '@totem/components/services/assetInventory/v2/checks';
import { getSystemTypeLabel } from '@totem/components/services/assetInventory/v2/configuration';
import {
  ControlSystemBackup,
  LabelCount,
  OsCount,
} from '@totem/components/services/assetInventory/v2/types';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';

import './assetInventoryV2.css';

const TOP_ROWS = 8;
const TOP_BUILDINGS = 6;

const percent = (value: number, total: number) =>
  total > 0 ? Math.round((value / total) * 100) : 0;

const latestBackup = (systems: ControlSystemBackup[]) =>
  systems
    .map((system) => system.lastBackup)
    .filter(Boolean)
    .sort((left, right) => dayjs(right).valueOf() - dayjs(left).valueOf())[0] ??
  null;

type KpiProps = {
  label: string;
  value: React.ReactNode;
  of?: number;
  sub: string;
  tone?: 'bad' | 'warn';
  small?: boolean;
  onClick?: () => void;
};

const Kpi = ({ label, value, of, sub, tone, small, onClick }: KpiProps) => {
  const valueStyle = [
    'kpi-value',
    small ? 'kpi-value-small' : '',
    tone ? `tone-${tone}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      <div styleName="kpi-label">{label}</div>
      <div styleName={valueStyle}>
        {value}
        {of !== undefined && <span styleName="kpi-of"> / {of}</span>}
      </div>
      <div styleName="kpi-sub">{sub}</div>
    </>
  );

  return onClick ? (
    <button type="button" styleName="kpi" onClick={onClick}>
      {content}
    </button>
  ) : (
    <div styleName="kpi kpi-static">{content}</div>
  );
};

const SummaryTab = () => {
  const { summary, showCheck, showDetails } = useContext(
    AssetInventoryV2Context,
  );
  const { handleFilter } = useContext(InputContext);

  if (!summary) {
    return null;
  }

  const { totals } = summary;
  const notLinked = totals.assets - totals.linked;
  const notManaged = totals.assets - totals.managed;
  const lastControllerBackup = latestBackup(summary.controlSystems);

  const rankedBars = <T extends LabelCount>(
    counts: T[],
    filterKey: string | null,
    missingLabel: string,
    // Rows that always get their own bar, beyond the top rows, and how each bar is drawn.
    alwaysShow: (item: T) => boolean = () => false,
    describe: (item: T) => Partial<BarItem> = () => ({}),
  ): BarItem[] => {
    const recorded = counts.filter((item) => item.label !== '');
    const missing = counts.find((item) => item.label === '');
    const top = recorded.filter(
      (item, idx) => idx < TOP_ROWS || alwaysShow(item),
    );
    const rest = recorded.filter((item) => !top.includes(item));
    const restCount = rest.reduce((total, item) => total + item.count, 0);
    const shownTotal = counts.reduce((total, item) => total + item.count, 0);
    const otherCount = restCount + (totals.assets - shownTotal);

    const items: BarItem[] = top.map((item) => ({
      key: item.label,
      label: item.label,
      segments: [{ value: item.count }],
      value: (
        <>
          <b>{item.count}</b> · {percent(item.count, totals.assets)}%
        </>
      ),
      onClick: filterKey
        ? () => handleFilter(filterKey, [item.label])
        : undefined,
      ...describe(item),
    }));

    if (otherCount > 0) {
      items.push({
        key: '__other',
        label: rest.length > 0 ? `Other (${rest.length} more)` : 'Other',
        segments: [{ value: otherCount, tone: 'grey' }],
        value: (
          <>
            <b>{otherCount}</b> · {percent(otherCount, totals.assets)}%
          </>
        ),
      });
    }

    if (missing) {
      items.push({
        key: '__missing',
        label: missingLabel,
        muted: true,
        segments: [{ value: missing.count, tone: 'grey' }],
        value: (
          <>
            <b>{missing.count}</b> · {percent(missing.count, totals.assets)}%
          </>
        ),
      });
    }

    return items;
  };

  const systemBars: BarItem[] = summary.bySystemType.map((item) => ({
    key: String(item.systemType),
    label: getSystemTypeLabel(item.systemType),
    segments: [
      { value: item.managed },
      { value: item.count - item.managed, tone: 'orange' },
    ],
    value: (
      <>
        <b>{item.count}</b> · {item.count - item.managed} unm.
      </>
    ),
    onClick: () => handleFilter('systemType', [String(item.systemType)]),
  }));

  if (notLinked > 0) {
    systemBars.push({
      key: '__notLinked',
      label: 'Not linked yet',
      muted: true,
      segments: [{ value: notLinked, tone: 'grey' }],
      value: (
        <>
          <b>{notLinked}</b> · {percent(notLinked, totals.assets)}%
        </>
      ),
      onClick: () => showCheck('notLinked'),
    });
  }

  const backupPill = (buildingId: string) => {
    const systems = summary.controlSystems.filter(
      (system) => system.buildingId === buildingId,
    );
    if (systems.length === 0) {
      return <span styleName="pill pill-grey">No system set up</span>;
    }
    const latest = latestBackup(systems);
    return latest ? (
      <span styleName="pill pill-amber">{formatDay(latest)}</span>
    ) : (
      <span styleName="pill pill-red">None on record</span>
    );
  };

  const serverSub =
    totals.servers === 0
      ? 'no servers in scope'
      : totals.serversBackedUpRecently === totals.servers
        ? `all ${totals.servers} servers covered`
        : `${totals.serversBackedUpRecently} of ${totals.servers} servers backed up in the last day`;

  const missingOs = summary.checks.noOperatingSystem?.count ?? 0;

  // Operating systems past (red) or within 12 months of (amber) end of support.
  const hasSupportIssue = (item: OsCount) =>
    item.pastEndOfSupport + item.endOfSupportSoon > 0;
  const describeOs = (item: OsCount): Partial<BarItem> => ({
    alert: item.pastEndOfSupport > 0,
    title: [
      item.label,
      item.pastEndOfSupport > 0 &&
        `${item.pastEndOfSupport} past end of support`,
      item.endOfSupportSoon > 0 &&
        `${item.endOfSupportSoon} support ends within 12 months`,
    ]
      .filter(Boolean)
      .join(' · '),
    segments: [
      { value: item.pastEndOfSupport, tone: 'red' },
      { value: item.endOfSupportSoon, tone: 'amber' },
      { value: item.count - item.pastEndOfSupport - item.endOfSupportSoon },
    ],
  });
  const osPast = summary.byOperatingSystem.filter(
    (item) => item.pastEndOfSupport > 0,
  );
  const osPastTotal = osPast.reduce(
    (total, item) => total + item.pastEndOfSupport,
    0,
  );
  const osSoonTotal = summary.byOperatingSystem.reduce(
    (total, item) => total + item.endOfSupportSoon,
    0,
  );

  return (
    <div styleName="pane">
      <div styleName="kpis">
        <Kpi
          label="Assets identified"
          value={totals.assets}
          sub={`${totals.buildings} building${totals.buildings === 1 ? '' : 's'}`}
          onClick={() => showDetails()}
        />
        <Kpi
          label="Under IB management"
          value={totals.managed}
          of={totals.assets}
          sub={`${notManaged} not managed`}
          onClick={() => showDetails('notManaged')}
        />
        <Kpi
          label="Past end of support"
          value={totals.pastEndOfSupport}
          of={totals.assets}
          tone={totals.pastEndOfSupport > 0 ? 'bad' : undefined}
          sub={`${totals.pastEndOfSupportManaged} under IB management · +${totals.endOfSupportSoon} ending within 12 months`}
          onClick={() => showCheck('pastEndOfSupport')}
        />
        <Kpi
          label="Computer backup coverage"
          value={totals.computersBackedUp}
          of={totals.computers}
          tone={
            totals.computersBackedUp < totals.computers ? 'warn' : undefined
          }
          sub={serverSub}
          onClick={() => showCheck('noBackup')}
        />
        <Kpi
          label="Controller backups"
          small
          value={
            summary.controlSystems.length === 0
              ? 'No system set up'
              : lastControllerBackup
                ? formatMonth(lastControllerBackup)
                : 'None on record'
          }
          tone={
            summary.controlSystems.length === 0
              ? undefined
              : !lastControllerBackup ||
                  dayjs(lastControllerBackup).isBefore(
                    dayjs().subtract(90, 'day'),
                  )
                ? 'warn'
                : undefined
          }
          sub="most recent on record"
        />
        <Kpi
          label="Service coverage"
          value={totals.withServiceContract}
          of={totals.assets}
          tone={
            totals.withServiceContract < totals.assets / 2 ? 'bad' : undefined
          }
          sub="assets with a support contract on file"
          onClick={() => showCheck('noServiceContract')}
        />
      </div>

      <div styleName="grid2">
        <div styleName="panel">
          <h4 styleName="panel-title">By building system</h4>
          <div styleName="panel-sub">
            What the asset supports · click to filter
          </div>
          <BarList items={systemBars} />
          <div styleName="legend">
            <span>
              <span
                styleName="legend-swatch"
                style={{ background: 'var(--ant-color-primary)' }}
              />
              Managed
            </span>
            <span>
              <span
                styleName="legend-swatch"
                style={{ background: '#f04b24' }}
              />
              Not managed
            </span>
            <span>
              <span
                styleName="legend-swatch"
                style={{ background: 'var(--ant-color-fill-secondary)' }}
              />
              Not linked
            </span>
          </div>
          {notLinked > 0 && (
            <div styleName="note">
              <b>
                {notLinked} asset{notLinked === 1 ? '' : 's'} (
                {percent(notLinked, totals.assets)}%){' '}
                {notLinked === 1 ? 'is' : 'are'} not linked to a building
                system.
              </b>{' '}
              That&apos;s why the building system shows blank for them today. IB
              is linking them so this view becomes complete.
            </div>
          )}
        </div>

        <div styleName="panel">
          <h4 styleName="panel-title">By building</h4>
          <div styleName="panel-sub">
            Top {Math.min(TOP_BUILDINGS, summary.byBuilding.length)} of{' '}
            {summary.byBuilding.length} by asset count
          </div>
          <div styleName="table-wrap">
            <table styleName="mini-table">
              <thead>
                <tr>
                  <th>Building</th>
                  <th styleName="num">Assets</th>
                  <th styleName="num">Managed</th>
                  <th styleName="num">Past support</th>
                  <th>Controller backup</th>
                </tr>
              </thead>
              <tbody>
                {summary.byBuilding.slice(0, TOP_BUILDINGS).map((building) => (
                  <tr key={building.buildingId}>
                    <td>
                      {building.buildingId ? (
                        <button
                          type="button"
                          styleName="link-button"
                          style={{ marginTop: 0, fontWeight: 400 }}
                          onClick={() =>
                            handleFilter('building', [building.buildingId])
                          }
                        >
                          {building.name}
                        </button>
                      ) : (
                        'No building'
                      )}
                    </td>
                    <td styleName="num">{building.count}</td>
                    <td styleName="num">
                      {building.managed}{' '}
                      <span styleName="sub">
                        ({percent(building.managed, building.count)}%)
                      </span>
                    </td>
                    <td styleName="num">{building.pastEndOfSupport}</td>
                    <td>{backupPill(building.buildingId)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div styleName="grid3">
        <div styleName="panel">
          <h4 styleName="panel-title">Device type</h4>
          <div styleName="panel-sub">Share of {totals.assets} assets</div>
          <BarList items={rankedBars(summary.byType, 'type', 'Not recorded')} />
        </div>
        <div styleName="panel">
          <h4 styleName="panel-title">Vendor</h4>
          <div styleName="panel-sub">Share of {totals.assets} assets</div>
          <BarList
            items={rankedBars(summary.byVendor, 'vendor', 'Not recorded')}
          />
        </div>
        <div styleName="panel">
          <h4 styleName="panel-title">Operating system</h4>
          <div styleName="panel-sub">Share of {totals.assets} assets</div>
          <BarList
            items={rankedBars(
              summary.byOperatingSystem,
              null,
              'Not recorded',
              hasSupportIssue,
              describeOs,
            )}
          />
          <div styleName="legend">
            <span>
              <span
                styleName="legend-swatch"
                style={{ background: 'var(--ant-color-primary)' }}
              />
              Supported or not checked
            </span>
            <span>
              <span
                styleName="legend-swatch"
                style={{ background: '#ef4444' }}
              />
              Past end of support
            </span>
            <span>
              <span
                styleName="legend-swatch"
                style={{ background: '#f59e0b' }}
              />
              Ends within 12 months
            </span>
          </div>
          {osPastTotal + osSoonTotal > 0 && (
            <div styleName="note note-red">
              {osPastTotal > 0 && (
                <>
                  <b>
                    {osPastTotal} asset{osPastTotal === 1 ? ' runs' : 's run'}{' '}
                    an operating system past end of support:
                  </b>{' '}
                  {osPast
                    .map((item) => `${item.label} (${item.pastEndOfSupport})`)
                    .join(', ')}
                  .{' '}
                </>
              )}
              {osSoonTotal > 0 &&
                `${osSoonTotal} more reach${osSoonTotal === 1 ? 'es' : ''} end of support within 12 months.`}
            </div>
          )}
          <div styleName="note note-blue">
            An operating system is normally not recorded for controllers and
            network gear.
            {missingOs > 0 &&
              ` ${missingOs} computer${missingOs === 1 ? ' is' : 's are'} missing one; ${missingOs === 1 ? 'it is' : 'those are'} flagged.`}{' '}
            Support dates are checked for Windows desktop and server releases
            and for operating systems with a date in the software catalog.
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryTab;
